import { useEffect, useRef, useState } from 'react';
import { Button } from '@dycs/ui';
import { askCsBot, type ChatTurn } from '@/lib/firebase';

interface ChatMessage {
  role: 'bot' | 'customer';
  text: string;
  /** 약매치 후보(Tier 3) — 클릭 시 재질문. */
  suggestions?: string[];
  /** 정류장 지도 링크 — 답변 본문이 아니라 별도 필드로 와서 그대로 렌더(URL 변형 없음). */
  links?: { label: string; url: string }[];
}

interface ChatbotPanelProps {
  tenantId: string;
  /** "상담원 접수"로 전환(접수 폼 노출 — source='chatbot'). */
  onEscalate: () => void;
  /** 대화가 바뀔 때마다 부모에 올린다 — 접수 시 티켓 스레드로 함께 넘긴다. */
  onHistoryChange?: (history: ChatTurn[]) => void;
}

const GREETING: ChatMessage = {
  role: 'bot',
  text: '안녕하세요! 무엇을 도와드릴까요? 궁금한 점을 입력해 주세요.',
};

/**
 * 챗봇 대화 — askCsBot(거래처 FAQ 근거) 호출, 말풍선 표시.
 *   해결 안 되면 '상담원 접수'(onEscalate)로 전환.
 */
/**
 * 서버로 보낼 대화 턴 — 인사말은 제외한다(고객이 한 말이 아니고, 문맥에 보태지 않는다).
 * 지도 링크 칩은 텍스트에 없으므로 자연히 빠진다.
 */
function toTurns(messages: ChatMessage[]): ChatTurn[] {
  return messages
    .slice(1)
    .filter((m) => m.text.trim())
    .map((m) => ({ role: m.role, text: m.text }));
}

export function ChatbotPanel({ tenantId, onEscalate, onHistoryChange }: ChatbotPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING]);
  const [input, setInput] = useState('');
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  // 대화가 늘 때마다 부모에 올린다 — 상담원 접수로 전환되면 티켓 스레드에 그대로 실린다.
  useEffect(() => {
    onHistoryChange?.(toTurns(messages));
  }, [messages, onHistoryChange]);

  async function send(text: string) {
    const q = text.trim();
    if (!q || sending) return;
    setInput('');
    // 이번 질문 **이전**까지의 대화를 문맥으로 보낸다(현재 발화는 text 로 따로 간다).
    const history = toTurns(messages);
    setMessages((prev) => [...prev, { role: 'customer', text: q }]);
    setSending(true);
    try {
      const res = await askCsBot({ tenantId, text: q, threadId, history });
      setThreadId(res.threadId);
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: res.answer, suggestions: res.suggestions, links: res.links },
      ]);
    } catch (err) {
      console.error('[widget] askCsBot 실패', err);
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: '죄송합니다. 답변을 가져오지 못했어요. 아래 \'상담원 접수\'로 문의해 주세요.',
        },
      ]);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col h-80">
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 p-1">
        {messages.map((m, i) =>
          m.role === 'customer' ? (
            <div
              key={i}
              className="max-w-[80%] ml-auto bg-accent text-accent-fg rounded-lg rounded-tr-sm px-3 py-2 text-sm whitespace-pre-wrap"
            >
              {m.text}
            </div>
          ) : (
            <div key={i} className="max-w-[85%] space-y-1.5">
              <div className="bg-muted rounded-lg rounded-tl-sm px-3 py-2 text-sm text-text-secondary whitespace-pre-wrap">
                {m.text}
              </div>
              {m.links && m.links.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[11.5px] text-text-tertiary px-0.5">
                    정류장 위치 — 눌러서 지도로 확인하세요.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {m.links.map((l) => (
                      <a
                        key={l.url}
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-2.5 py-1 rounded-full border border-border-default hover:border-accent hover:bg-accent-softer transition-colors"
                      >
                        📍 {l.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {m.suggestions && m.suggestions.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {m.suggestions.map((s, si) => (
                    <button
                      key={si}
                      type="button"
                      className="text-xs px-2.5 py-1 rounded-full border border-border-default hover:border-accent hover:bg-accent-softer transition-colors text-left"
                      onClick={() => void send(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ),
        )}
        {sending && (
          <div className="max-w-[80%] bg-muted rounded-lg rounded-tl-sm px-3 py-2 text-sm text-text-disabled">
            답변을 작성하고 있어요…
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-border-subtle space-y-2">
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
        >
          <input
            className="flex-1 px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none disabled:opacity-50"
            placeholder="메시지를 입력하세요."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={sending}
          />
          <Button type="submit" loading={sending} disabled={!input.trim()}>
            전송
          </Button>
        </form>
        <button
          type="button"
          className="w-full text-center text-xs text-text-tertiary hover:text-accent transition-colors"
          onClick={onEscalate}
        >
          답변이 부족한가요? 상담원에게 직접 접수하기 →
        </button>
      </div>
    </div>
  );
}
